import { Children, forwardRef, useRef } from 'react';
import cn from 'classnames';
import { ChevronRight, ChevronLeft } from '@vgno/icons';

import { ListHeader } from '../ListHeader/ListHeader';
import { isTouchDevice } from '../../utils/device';

import { useSliderDragging } from './useSliderDragging';
import { useSentinel } from './useSentinel';

import type { PropsWithChildren, ReactNode } from 'react';

import * as styles from './Slider.module.scss';

export interface SliderProps {
  header?: ReactNode;
  headerButtonLabel?: ReactNode;
  disableHeaderContentClip?: boolean;
  onHeaderButtonClick?: () => void;
  itemClassName?: string;
  className?: string;
}

const Slider = forwardRef<HTMLDivElement, PropsWithChildren<SliderProps>>(
  (
    {
      header,
      headerButtonLabel,
      disableHeaderContentClip,
      onHeaderButtonClick,
      children,
      itemClassName,
      className,
    },
    ref
  ) => {
    const sliderRef = useRef<HTMLDivElement>(null!);
    const startSentinelRef = useRef<HTMLDivElement>(null!);
    const endSentinelRef = useRef<HTMLDivElement>(null!);

    const { isIntersecting: atStart } = useSentinel(startSentinelRef);
    const { isIntersecting: atEnd } = useSentinel(endSentinelRef);

    const { handleMouseDown, handleClickCapture, handleDragStartCapture } =
      useSliderDragging<HTMLElement>({ sliderRef });

    const hasHeaderButton = headerButtonLabel && onHeaderButtonClick;
    const itemsToRender = Children.toArray(children);

    const handleSideArrowClick = (direction: 'left' | 'right') => {
      const multiplier = direction === 'left' ? -1 : 1;

      sliderRef.current.scrollBy({
        left: multiplier * sliderRef.current.clientWidth,
        behavior: 'smooth',
      });
    };

    return (
      <div className={cn(styles.slider, className)} ref={ref}>
        {header || hasHeaderButton ? (
          <ListHeader
            headerContent={header}
            buttonLabel={headerButtonLabel}
            onButtonClick={onHeaderButtonClick}
            disableContentClip={disableHeaderContentClip}
          />
        ) : null}
        <div
          className={styles.listWrapper}
          ref={sliderRef}
          onMouseDown={!isTouchDevice ? handleMouseDown : undefined}
          onClickCapture={!isTouchDevice ? handleClickCapture : undefined} // use capture to actually prevent clicks
          onDragStartCapture={
            !isTouchDevice ? handleDragStartCapture : undefined
          }
        >
          {!atStart && !isTouchDevice && (
            <button
              className={cn(styles.sideArrow, styles.sideArrowLeft)}
              aria-hidden
              tabIndex={-1}
              onClick={() => handleSideArrowClick('left')}
            >
              <ChevronLeft />
            </button>
          )}
          <ul className={styles.list}>
            <div ref={startSentinelRef} className={styles.listSentinel} />
            {itemsToRender.map((childItem, index) => (
              <li key={index} className={cn(styles.listItem, itemClassName)}>
                {childItem}
              </li>
            ))}
            <div ref={endSentinelRef} className={styles.listSentinel} />
          </ul>
          {!atEnd && !isTouchDevice && (
            <button
              className={cn(styles.sideArrow, styles.sideArrowRight)}
              aria-hidden
              tabIndex={-1}
              onClick={() => handleSideArrowClick('right')}
            >
              <ChevronRight />
            </button>
          )}
        </div>
      </div>
    );
  }
);
Slider.displayName = 'Slider';

export { Slider };
